/**
 * Source parameter capture and persistence.
 *
 * Captures Hyros-style source parameters (sl=, el=, htrafficsource=)
 * and standard UTM parameters from the URL. Persists them in sessionStorage
 * so that subsequent events in the same session inherit the same source.
 */

import { debugLog } from "./state.js";

const STORAGE_KEY_SOURCE = "_tst_src";

/** All source-related URL parameters we capture. */
const SOURCE_PARAMS = [
  "sl",               // Paid source tag (results in source @name)
  "el",               // Organic/manual source tag
  "htrafficsource",   // Traffic source grouping
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  // Our own ids, filled by the platform's URL macros.
  //
  // A utm_campaign is a NAME the advertiser typed, so it breaks the moment they
  // rename the campaign, and it is missing entirely on any account that never
  // tagged anything. Every ad platform can substitute its own campaign and ad id
  // into a landing URL, and those never change. `tf_` rather than a utm name
  // because these are not utm parameters: putting an id in utm_content would
  // make the ad level of the report a column of numbers.
  //
  // Google fills these itself through click_view and needs no macro. For every
  // other platform the template generator produces the tracking template, which
  // is the one thing a customer still has to paste.
  "tf_cid",           // The platform's campaign id
  "tf_aid",           // The platform's ad or creative id
  // ESP fingerprint params: email detection without any UTM setup.
  "mc_cid",           // Mailchimp campaign id (always appended)
  "mc_eid",           // Mailchimp recipient id
  "_kx",              // Klaviyo recipient token
  "vgo_ee",           // ActiveCampaign site-tracking token
  "_hsenc",           // HubSpot
  "_hsmi",            // HubSpot
] as const;

export type SourceParams = Partial<Record<typeof SOURCE_PARAMS[number], string>>;

/** Parse source parameters from the current URL. */
function parseSourceParams(): SourceParams | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const result: Record<string, string> = {};
    let found = false;

    for (let i = 0; i < SOURCE_PARAMS.length; i++) {
      const key = SOURCE_PARAMS[i];
      // "?utm_source": the whole template, question mark included, pasted
      // into Meta's separate URL-parameters field. Meta encodes the string
      // and the mark becomes part of the first key, so utm_source vanished
      // while the other tags read fine (2 September 2026, thesocialtap.app).
      const value = params.get(key) || params.get("?" + key);
      if (value) {
        result[key] = value;
        found = true;
      }
    }

    if (!found) return null;
    return result;
  } catch {
    return null;
  }
}

/** Store source params in sessionStorage. */
function storeSourceParams(params: SourceParams): void {
  try {
    sessionStorage.setItem(STORAGE_KEY_SOURCE, JSON.stringify(params));
  } catch {
    /* sessionStorage not available */
  }
}

/** Load source params from sessionStorage. */
function loadStoredSourceParams(): SourceParams | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY_SOURCE);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Capture source parameters from the URL and persist in sessionStorage.
 * Called eagerly during script initialization (before first event).
 *
 * If URL has source params → store them (overrides any previous session source).
 * If URL has no source params → keep whatever is in sessionStorage.
 */
export function captureSourceParams(): void {
  const urlParams = parseSourceParams();
  if (urlParams) {
    debugLog("captureSourceParams", "found in URL", urlParams);
    storeSourceParams(urlParams);
  }
}

/**
 * Get the current source parameters for inclusion in event payloads.
 * Returns URL params if present on current page, otherwise falls back to stored session params.
 */
export function getSourceParams(): SourceParams | null {
  const urlParams = parseSourceParams();
  if (urlParams) return urlParams;
  return loadStoredSourceParams();
}
